import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatDateTime } from '../../../utils/formatters';

const NotificationHistory = ({ notifications }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle', label: 'Sent' },
      draft: { color: 'bg-muted/10 text-muted-foreground border-muted/20', icon: 'FileText', label: 'Draft' },
      scheduled: { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock', label: 'Scheduled' },
      failed: { color: 'bg-destructive/10 text-destructive border-destructive/20', icon: 'XCircle', label: 'Failed' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  const getRecipientTypeLabel = (type) => {
    const labels = {
      all_reviewers: 'All Reviewers',
      all_authors: 'All Authors',
      paper_reviewers: 'Paper Reviewers',
      paper_authors: 'Paper Author',
      role_based: 'Role-Based',
      specific_users: 'Specific Users'
    };
    return labels?.[type] || type;
  };

  const formatDateTimeSafe = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString) || 'N/A';
  };

  if (notifications?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Notifications Yet</h3>
        <p className="text-sm text-muted-foreground">
          Notifications you send will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notifications?.map(notification => (
        <div
          key={notification?.id}
          className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-foreground">{notification?.subject}</h3>
                {getStatusBadge(notification?.status)}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">{notification?.message}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Type</span>
              <span className="text-sm font-medium text-foreground capitalize">
                {notification?.notificationType?.replace('_', ' ')}
              </span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Recipients</span>
              <span className="text-sm font-medium text-foreground">
                {getRecipientTypeLabel(notification?.recipientType)}
              </span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Total Sent</span>
              <span className="text-sm font-medium text-foreground">
                {notification?.totalRecipients || 0}
              </span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Success Rate</span>
              <span className="text-sm font-medium text-foreground">
                {notification?.totalRecipients > 0
                  ? `${Math.round((notification?.successfulSends / notification?.totalRecipients) * 100)}%`
                  : 'N/A'}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <span>
                <Icon name="User" size={12} className="inline mr-1" />
                {notification?.createdByName}
              </span>
              <span>
                <Icon name="Calendar" size={12} className="inline mr-1" />
                {formatDateTimeSafe(notification?.sentAt || notification?.createdAt)}
              </span>
            </div>
            {notification?.failedSends > 0 && (
              <span className="text-xs text-destructive">
                <Icon name="AlertCircle" size={12} className="inline mr-1" />
                {notification?.failedSends} failed
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationHistory;